import type { BadgeProps } from '@/shared/ui/badge';
import type { ReferralStatus, RewardStatus } from './types';

type BadgeVariant = NonNullable<BadgeProps['variant']>;

interface StatusMeta {
  label: string;
  variant: BadgeVariant;
}

/**
 * Status da indicação (spec, seção 6): Indicada → Cadastrada → Convertida.
 * Os ícones (animados) ficam na UI: `ui/status-icons.ts`.
 */
export const REFERRAL_STATUS: Record<ReferralStatus, StatusMeta> = {
  indicated: { label: 'Indicada', variant: 'info' },
  registered: { label: 'Cadastrada', variant: 'accent' },
  converted: { label: 'Convertida', variant: 'success' },
  rejected: { label: 'Não elegível', variant: 'secondary' },
};

/** Status da recompensa (spec, seção 7), separado do status da indicação. */
export const REWARD_STATUS: Record<RewardStatus, StatusMeta> = {
  pending: { label: 'Pendente', variant: 'warning' },
  granted: { label: 'Concedida', variant: 'success' },
  reversed: { label: 'Estornada', variant: 'destructive' },
  cancelled: { label: 'Cancelada', variant: 'secondary' },
  failed: { label: 'Com falha', variant: 'destructive' },
};

/** Ordem da trilha na tela do participante e na timeline. */
export const REFERRAL_STEPS: ReferralStatus[] = ['indicated', 'registered', 'converted'];

/** Quantos passos da trilha já foram cumpridos (rejeitada para no 1º). */
export function stepsDone(status: ReferralStatus): number {
  if (status === 'converted') return 3;
  if (status === 'registered') return 2;
  return 1;
}

const REJECT_REASONS: Record<string, string> = {
  shared_contact: 'Mesmo telefone ou e-mail de quem indicou',
  self_referral: 'Autoindicação',
};

export function rejectReasonLabel(reason: string | null | undefined): string {
  if (!reason) return 'Não elegível';
  return REJECT_REASONS[reason] ?? 'Não elegível';
}

const EVENT_LABELS: Record<string, string> = {
  indicated: 'Indicação criada pelo link',
  reassigned: 'Reatribuída (a campanha anterior acabou)',
  campaign_switched: 'Passou para a campanha em vigor',
  registered: 'Indicado se cadastrou',
  rejected: 'Rejeitada no cadastro',
  purchase_detected: 'Primeira compra detectada',
  converted: 'Indicação convertida',
  purchase_cancelled: 'Compra cancelada ou estornada',
  conversion_reverted: 'Conversão desfeita',
  purchase_window_expired: 'Prazo da primeira compra terminou',
  clawback_window_closed: 'Janela de estorno encerrada',
  reward_granted: 'Recompensa concedida',
  reward_reversed: 'Recompensa estornada',
  reward_cancelled: 'Recompensa cancelada',
};

export function eventLabel(type: string): string {
  return EVENT_LABELS[type] ?? type;
}

/** 0.833 -> "83,3%"; null -> "—" (sem base, não é 0%). */
export function formatRate(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return `${(value * 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;
}
